import type { CardCatalog, TerritoryCatalog } from '../types/catalog';

/**
 * Base da API. Em produção, defina `VITE_API_BASE_URL` com a origem do
 * card-inventory-server. Em desenvolvimento, deixe vazio: o proxy do Vite
 * (vite.config.ts) encaminha `/api` para http://localhost:5001.
 */
const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/**
 * Base do auth-server, que recebe o formulário "Falar com os desenvolvedores".
 * Em dev deixe vazio: o proxy do Vite encaminha `/v1` para http://localhost:5000.
 */
const AUTH_BASE_URL: string = (import.meta.env.VITE_AUTH_BASE_URL ?? '').replace(/\/$/, '');

/**
 * Base para resolver URLs de arte relativas (ex.: "/assets/cards/x.png").
 * Cartas que trazem uma URL absoluta são usadas como estão.
 */
const ASSET_BASE_URL: string = (import.meta.env.VITE_ASSET_BASE_URL ?? API_BASE_URL).replace(/\/$/, '');

/** Monta a URL completa de um endpoint da API. */
function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

/**
 * Resolve a `artUrl` de uma carta/território para uma URL utilizável pelo
 * navegador. URLs absolutas (http/https/data) passam direto; relativas são
 * prefixadas pela base de assets.
 */
export function resolveAssetUrl(artUrl: string | undefined | null): string | null {
  if (!artUrl) return null;
  if (/^(https?:|data:|blob:)/i.test(artUrl)) return artUrl;
  const path = artUrl.startsWith('/') ? artUrl : `/${artUrl}`;
  return `${ASSET_BASE_URL}${path}`;
}

/** Erro de rede/HTTP com informação suficiente para a UI exibir. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      // 'no-cache' = sempre revalida com o servidor (ETag/304) em vez de confiar
      // no `Cache-Control: max-age=3600` que o catálogo envia. Uma resposta CORS
      // em cache carrega o `Access-Control-Allow-Origin` da origem que a buscou
      // primeiro; reaproveitá-la de outra origem (ex.: depois de visitar
      // play.gameofbrasil.com) quebra a checagem de CORS no navegador — o que
      // acontecia no mobile. O cliente do jogo (CatalogLoader) faz o mesmo.
      cache: 'no-cache',
      headers: { Accept: 'application/json' },
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new ApiError(
      `Não foi possível alcançar o servidor de catálogo. Verifique se o card-inventory-server está no ar${
        API_BASE_URL ? ` em ${API_BASE_URL}` : ''
      }.`,
    );
  }
  if (!res.ok) {
    throw new ApiError(`A API respondeu com erro ${res.status} ao buscar ${path}.`, res.status);
  }
  return (await res.json()) as T;
}

/** GET /api/cards/catalog — catálogo público de cartas. */
export function fetchCardCatalog(signal?: AbortSignal): Promise<CardCatalog> {
  return getJson<CardCatalog>('/api/cards/catalog', signal);
}

/** GET /api/territories/catalog — catálogo público de territórios. */
export function fetchTerritoryCatalog(signal?: AbortSignal): Promise<TerritoryCatalog> {
  return getJson<TerritoryCatalog>('/api/territories/catalog', signal);
}

/** Dados do formulário "Falar com os desenvolvedores". */
export interface ContactInput {
  name: string;
  /** Opcional. */
  email?: string;
  subject: string;
  message: string;
}

/**
 * POST /v1/contact no auth-server — envia uma mensagem para os desenvolvedores.
 * Diferente do catálogo, isto vai para a base do auth-server (AUTH_BASE_URL).
 */
export async function postContact(input: ContactInput, signal?: AbortSignal): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${AUTH_BASE_URL}/v1/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(input),
      ...(signal ? { signal } : {}),
    });
  } catch {
    throw new ApiError('Não foi possível enviar a mensagem. Verifique sua conexão e tente novamente.');
  }
  if (res.status === 429) {
    throw new ApiError('Você enviou muitas mensagens. Aguarde um pouco e tente novamente.', 429);
  }
  if (!res.ok) {
    throw new ApiError('Não foi possível enviar a mensagem. Tente novamente mais tarde.', res.status);
  }
}
